// The unplaced / invalid strip (DESIGN D6): at the end of the control plane, a visible red-bordered strip listing every
// check that landed on no asset (grid.unplaced), every model grid entry the API could not place (grid.invalid), any
// asset this page could not position, and any check the answer carries under a monitor but places nowhere. Drawn only
// when there is something in it - and then never hidden or collapsed.
import type { HealthGridAsset } from "./types";
import type { GridCtx } from "./ctx";
import { AssetTile } from "./AssetTile";
import { CheckTile } from "./Tiles";
import { plural } from "./present";

export function UnplacedStrip({ ctx, unplaceable }: { ctx: GridCtx; unplaceable: { asset: HealthGridAsset; why: string }[] }) {
  const { unplaced, orphans } = ctx.placement;
  const invalid = ctx.data.grid.invalid;
  const total = unplaced.length + orphans.length + invalid.length + unplaceable.length;
  if (total === 0) return null;
  // R4 D2: per-pipeline readings the API moved off a NOT APPLICABLE asset are drawn here too (they are no checks)
  const readings = unplaced.filter(p => typeof p.tile.evidence_of === "string").length;
  return (
    <section className="ph-unplaced" aria-labelledby="ph-unplaced-title" data-testid="unplaced-strip">
      <h3 id="ph-unplaced-title">NOT ON THE GRID - {plural(total, "item")}</h3>
      <p className="ph-hint">listed here rather than dropped. Each is shown with what the answer says about it.</p>
      {unplaced.length > 0 ? (
        <div data-testid="unplaced-checks">
          <h4 className="ph-panel-sub">
            Checks that landed on no asset ({unplaced.length - readings}){readings > 0 ? ` + per-pipeline readings moved off a NOT APPLICABLE asset (${readings})` : ""}
          </h4>
          <p className="ph-note">
            each landed on no asset, or was moved off a NOT APPLICABLE one - they are counted in the band as one entry, "unplaced checks".
          </p>
          <div className="ph-checks">
            {unplaced.map((p, i) => {
              const why = typeof p.tile.unplaced_reason === "string" && p.tile.unplaced_reason.trim() !== "" ? p.tile.unplaced_reason.trim() : null;
              return (
                <div key={`u-${i}`} className="ph-unplaced-item" data-evidence-of={typeof p.tile.evidence_of === "string" ? p.tile.evidence_of : undefined}>
                  <p className="ph-note" data-testid="unplaced-reason">
                    why here: {why ?? "the answer gives no reason"}
                  </p>
                  <CheckTile tile={p.tile} from={p.monitor} reading={p.reading === true} where={why ?? "landed on no asset - the answer gives no reason"} />
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      {orphans.length > 0 ? (
        <div data-testid="orphan-checks">
          <h4 className="ph-panel-sub">Checks the answer lists under a monitor but places nowhere ({orphans.length})</h4>
          <p className="ph-contra">the answer contradicts itself: every check should land on exactly one asset or be listed as unplaced.</p>
          <div className="ph-checks">
            {orphans.map((p, i) => (
              <CheckTile key={`o-${i}`} tile={p.tile} from={p.monitor} where="placed nowhere by the answer" />
            ))}
          </div>
        </div>
      ) : null}
      {unplaceable.length > 0 ? (
        <div data-testid="unplaceable-assets">
          <h4 className="ph-panel-sub">Assets this page could not position ({unplaceable.length})</h4>
          <div className="ph-cell ph-cell-strip">
            {unplaceable.map(({ asset, why }) => (
              <div key={asset.id} className="ph-unplaceable">
                <AssetTile a={asset} />
                <p className="ph-note">{why}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {invalid.length > 0 ? (
        <div data-testid="invalid-entries">
          <h4 className="ph-panel-sub">Grid entries the API could not place ({invalid.length})</h4>
          <ul className="ph-plain-list">
            {invalid.map((x, i) => (
              <li key={`i-${i}`} data-invalid={x.id ?? ""} data-invalid-entry={typeof x.entry === "string" ? x.entry : undefined}>
                <code>{x.id ?? "(no id)"}</code> - {x.why}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
